
import React from "react";
import { Link } from "react-router-dom";
import { Star, Target, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CollapsibleSection from "@/components/dashboard/CollapsibleSection";
import { motion } from "framer-motion";
import { useFavorites } from "@/hooks/useFavorites";
import { useTranslation } from "react-i18next";
import { puttingTests, chippingTests, wedgingTests, bunkerTests, ironTests, fairwayWoodTests } from "@/data/level3/tests";
import { driverTests } from "@/data/level3/driverTests";
// Import des tests level1
import { 
  puttingTests as level1PuttingTests, 
  chippingTests as level1ChippingTests, 
  wedgingTests as level1WedgingTests, 
  bunkerTests as level1BunkerTests, 
  ironTests as level1IronTests, 
  fairwayWoodTests as level1FairwayWoodTests, 
  driverTests as level1DriverTests 
} from "@/data/level1/tests";
// Import des tests level2
import { 
  puttingTests as level2PuttingTests, 
  chippingTests as level2ChippingTests, 
  wedgingTests as level2WedgingTests, 
  bunkerTests as level2BunkerTests, 
  ironTests as level2IronTests, 
  fairwayWoodTests as level2FairwayWoodTests, 
  driverTests as level2DriverTests 
} from "@/data/level2/tests";

interface FavoriteTestsSectionProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const FavoriteTestsSection: React.FC<FavoriteTestsSectionProps> = ({ 
  isCollapsed, 
  onToggle 
}) => {
  const { favorites, isLoading } = useFavorites();
  const { t } = useTranslation('dashboard');
  
  // Combine all tests for lookup including level1 and level2 tests
  const allTests = [
    ...puttingTests, 
    ...chippingTests, 
    ...wedgingTests, 
    ...bunkerTests, 
    ...ironTests, 
    ...fairwayWoodTests, 
    ...driverTests,
    // Ajouter les tests level1
    ...level1PuttingTests,
    ...level1ChippingTests,
    ...level1WedgingTests,
    ...level1BunkerTests,
    ...level1IronTests,
    ...level1FairwayWoodTests,
    ...level1DriverTests,
    // Ajouter les tests level2
    ...level2PuttingTests,
    ...level2ChippingTests,
    ...level2WedgingTests,
    ...level2BunkerTests,
    ...level2IronTests,
    ...level2FairwayWoodTests,
    ...level2DriverTests
  ];
  
  // Get up to 4 favorites (changed from 2 to 4)
  const getDisplayFavorites = () => {
    if (favorites.length <= 4) return favorites;
    return [...favorites].sort(() => 0.5 - Math.random()).slice(0, 4);
  };
  
  const displayFavorites = getDisplayFavorites();
  
  // Get test details by ID
  const getTestDetails = (testId: string) => {
    return allTests.find(test => test.id === testId);
  };
  
  // Get route based on test type
  const getTestRoute = (testType: string, testId: string) => {
    if (testType === "level3" || testType === "level2" || testType === "level1") {
      return `/training/${testType}?test=${testId}`;
    } else if (testType === "putting") {
      // Pour les exercices de putting, trouver l'ID numérique
      const numId = parseInt(testId.split('-')[1], 10);
      return `/putting`;
    } else if (testType === "approach") {
      return `/approaches`;
    } else if (testType === "chipping") {
      return `/training/chipping`;
    } else {
      return `/training/${testType}`;
    }
  };

  // Fonction pour obtenir le nom du niveau
  const getLevelName = (testType: string) => {
    switch (testType) {
      case "level1":
        return t('levels.level1');
      case "level2":
        return t('levels.level2');
      case "level3":
        return t('levels.level3');
      default:
        return testType;
    }
  };

  // Fonction pour obtenir la couleur du badge selon le niveau
  const getLevelBadgeVariant = (testType: string) => {
    switch (testType) {
      case "level1":
        return "secondary";
      case "level2":
        return "default";
      case "level3":
        return "outline";
      default:
        return "secondary";
    }
  };

  return (
    <CollapsibleSection 
      title={t('favorites.title')}
      icon={<Star className="text-golf-600" />} 
      isCollapsed={isCollapsed} 
      onToggle={onToggle}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {isLoading ? (
          <Card className="col-span-full">
            <CardContent className="flex items-center justify-center p-4">
              <p className="text-muted-foreground">{t('favorites.loading')}</p>
            </CardContent>
          </Card>
        ) : favorites.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="flex flex-col items-center justify-center p-4">
              <p className="text-muted-foreground mb-2">{t('favorites.noFavorites')}</p>
              <p className="text-sm text-muted-foreground">{t('favorites.noFavoritesHelp')}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {displayFavorites.map((favorite) => {
              const testDetails = getTestDetails(favorite.test_id);
              if (!testDetails) return null;
              
              return (
                <Link 
                  key={favorite.id} 
                  to={getTestRoute(favorite.test_type, favorite.test_id)}
                >
                  <Card 
                    className="bg-golf-50 border-golf-200 hover:bg-golf-100 transition-colors duration-200"
                  >
                    <CardContent className="flex items-center p-4">
                      <Target className="h-8 w-8 text-golf-600 mr-3" />
                      <div className="flex-1">
                        <h3 className="font-medium text-golf-800">{testDetails.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={getLevelBadgeVariant(favorite.test_type)} className="text-xs">
                            {getLevelName(favorite.test_type)}
                          </Badge>
                          <p className="text-sm text-muted-foreground">{testDetails.description.substring(0, 40)}...</p>
                        </div>
                      </div>
                      <ChevronRight className="ml-auto text-golf-400" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
            
            <Card className="md:col-span-2">
              <CardContent className="py-4 text-center">
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/training">
                    {t('favorites.manage')} <ChevronRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default FavoriteTestsSection;
